<script lang="ts">
	// IMPORTS
	import FacebookLogo from '$components/PlatformLogos/FacebookLogo.svelte';
	import InstagramLogo from '$components/PlatformLogos/InstagramLogo.svelte';
	import NewsletterLogo from '$components/PlatformLogos/NewsletterLogo.svelte';
	import PodcastLogo from '$components/PlatformLogos/PodcastLogo.svelte';
	import RssLogo from '$components/PlatformLogos/RssLogo.svelte';
	import TwitterLogo from '$components/PlatformLogos/TwitterLogo.svelte';
	import YoutubeLogo from '$components/PlatformLogos/YoutubeLogo.svelte';
	import { ChevronDown } from 'lucide-svelte';
	import { slugify } from '$lib/slugify';
	import { slide } from 'svelte/transition';

	interface Platform {
		name: string;
		url: string;
	}

	interface Link {
		link: string;
		url: string;
	}

	interface Section {
		type: string;
		title: string;
		platforms?: Platform[];
		links?: Link[];
	}

	interface FooterData {
		sections: Section[];
		copyright: string;
	}

	// PROPS
	export let data: FooterData;

	// VARIABLES
	const sections = data.sections;

	// LOGIC
	function toggleMenu(event: MouseEvent) {
		console.log('toggleMenu');
		const menuItem = event.currentTarget as HTMLElement;
		const itemInner = menuItem.nextElementSibling as HTMLElement;
		itemInner.classList.toggle('collapsed');
		itemInner.classList.toggle('expanded');
	}
</script>

<footer>
	<ul class="footer-menu">
		{#each sections as section}
			{#if section.type === 'follow'}
				<li id="{slugify(section.title)}" class="menu-item menu-social">
					<span class="item-title">{section.title}</span>
					<div class="item-inner">
						<ul class="subitems subitems-social">
							{#if section.type === 'follow' && section.platforms}
								{#each section.platforms as platform}
									{#if platform.name === 'facebook'}
										<FacebookLogo url="{platform.url}" variant="dark" />
									{:else if platform.name === 'twitter'}
										<TwitterLogo url="{platform.url}" variant="dark" />
									{:else if platform.name === 'youtube'}
										<YoutubeLogo url="{platform.url}" variant="dark" />
									{:else if platform.name === 'instagram'}
										<InstagramLogo url="{platform.url}" variant="dark" />
									{:else if platform.name === 'rss'}
										<RssLogo url="{platform.url}" variant="dark" />
									{:else if platform.name === 'podcasts'}
										<PodcastLogo url="{platform.url}" variant="dark" />
									{:else if platform.name === 'newsletters'}
										<NewsletterLogo url="{platform.url}" variant="dark" />
									{/if}
								{/each}
							{/if}
						</ul>
					</div>
				</li>
			{:else}
				<li id="{slugify(section.title)}" class="menu-item menu-links">
					<!-- svelte-ignore a11y-no-static-element-interactions -->
					<span on:click="{toggleMenu}" class="item-title"
						>{section.title} <ChevronDown /></span
					>
					<div class="item-inner collapsed" transition:slide>
						<div class="subitems subitems-links">
							{#if section.links}
								<ul>
									{#each section.links as linkObj}
										<li class="subitem">
											<a href="{linkObj.url}">{linkObj.link}</a>
										</li>
									{/each}
								</ul>
							{/if}
						</div>
					</div>
				</li>
			{/if}
		{/each}
	</ul>
	<div class="cp">{data.copyright}</div>
</footer>

<style lang="scss">
	@use '$styles/mq' as *;

	footer {
		contain: content;
		background: var(--footer-bg);
		padding-bottom: 16px;
		color: white;

		@include sizes(medium) {
			padding-right: 12px;
			padding-left: 12px;
		}
	}

	.cp {
		margin-top: 12px;
		margin-right: 10px;
		padding-top: 12px;
		font-weight: 200;
		font-size: 13px;
		font-family: var(--brand-font-copy);
		text-align: right;

		@include sizes(medium) {
			border-top: 1px solid #b36123;
		}

		@include sizes(wide) {
			border-top: none;
		}
	}

	ul.footer-menu {
		font-size: 15.4px;
		font-family: var(--brand-font-ui-cn);

		@include sizes(medium) {
			font-size: 19.4px;
		}

		@include sizes(wide) {
			display: grid;
			grid-template-columns: 25% 25% 50%;
			grid-template-areas: 'col1 col2 col3';
			grid-auto-rows: minmax(100px, auto);
			align-items: start;
			margin: 0;
			margin: 0 auto;
			padding: 0;
			max-width: 700px;

			.menu-links {
				grid-area: col1;
			}

			.menu-links + .menu-links {
				grid-area: col2;
			}

			.menu-social {
				grid-area: col3;
			}
		}
	}

	.menu-links {
		cursor: pointer;

		@include sizes(medium) {
			cursor: default;
		}
	}

	.menu-links span.item-title {
		border-top: 1px solid #b36123;
		background: #873c02;

		&:hover {
			background: #6e3101;
		}

		&:last-child {
			border-bottom: 1px solid #b36123;
		}

		@include sizes(medium) {
			border-top: 1px solid #b36123;
			background: var(--footer-bg);

			&:hover {
				background: inherit;
			}

			&:last-child {
				border-bottom: none;
			}
		}
	}

	:global(.menu-links .lucide-chevron-down) {
		position: absolute;
		top: 50%;
		right: 10px;
		margin-top: -10px;

		@include sizes(medium) {
			display: none;
		}
	}

	li span.item-title {
		display: block;
		position: relative;
		padding: 12px;
		font-weight: bold;
		text-transform: uppercase;

		@include sizes(medium) {
			padding-top: 16px;
			padding-bottom: 16px;
			padding-left: 0;
		}
	}

	.menu-links ul {
		@include sizes(medium) {
			column-gap: 10px;
			column-rule-style: none;
			column-count: 2;
			list-style: none;
		}

		@include sizes(wide) {
			column-count: 1;
		}
	}

	.subitems-links .subitem {
		break-inside: avoid;
		cursor: pointer;
		border-top: 1px solid #9d561f;
		background: #552501;
		padding: 10px 12px;

		&:hover {
			background-color: #3c1a01;
		}

		a {
			border-left: 2px solid #9d561f;
			padding: 6px 12px;
		}

		@include sizes(medium) {
			border-top: none;
			background: inherit;
			padding-right: 0;
			padding-left: 0;

			&:hover {
				background: inherit;
				text-decoration: underline;
			}

			a {
				display: inline;
				border-left: none;
				padding: 0;
			}
		}
	}

	.subitem {
		font-weight: bold;
		text-transform: uppercase;

		@include sizes(medium) {
			font-weight: 200;
			font-size: 15.4px;
			font-family: var(--brand-font-copy);
			text-transform: none;
		}
	}

	.item-inner {
		@include sizes(medium) {
			padding-top: 8px;
			padding-bottom: 8px;
		}
	}

	:global(.item-inner.collapsed) {
		display: none;

		@include sizes(medium) {
			display: block;
		}
	}

	:global(.item-inner.expanded) {
		display: block;
	}

	:global(.item-title.expanded .lucide-chevron-down) {
		transform: rotate(180deg);
	}

	ul.subitems-social {
		display: flex;
		flex-wrap: wrap;
		justify-content: flex-start;
		margin-bottom: 8px;
		padding-right: 12px;
		padding-bottom: 8px;
		padding-left: 12px;

		@include sizes(medium) {
			padding-right: 0;
			padding-left: 0;
		}
	}

	:global(.platform-link) {
		margin-right: 8px;
	}
</style>
